import React, { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { authFetch } from '../utils/authHelpers';
import { AI_ENGINE_URL } from '../utils/apiConfig';
import { resolveToolName } from '../config/navigation';
import { veiligHandoffPad } from '../utils/assistantHandoff';
import toast from 'react-hot-toast';

const STORAGE_KEY = 'ff_assistant_conv_id';

const AssistantContext = createContext({
  isOpen: false,
  conversationId: null,
  messages: [],
  isStreaming: false,
  pendingAction: null,
  toolStatus: null,
  toggle: () => {},
  sluit: () => {},
  sendMessage: () => {},
  confirmAction: () => {},
  cancelAction: () => {},
  stopStreaming: () => {},
  setConversationId: () => {},
});

// Alleen open/dicht, los van de berichten: het TaakPaneel luistert hiernaar (die twee
// panelen rechts sluiten elkaar uit) en hoeft dan niet bij elk gestreamd token te hertekenen.
const AssistantZichtbaarContext = createContext({ isOpen: false, sluit: () => {} });

export function AssistantProvider({ children }) {
  const { accounts } = useMsal();
  const userEmail = accounts?.[0]?.username || '';
  const userName = accounts?.[0]?.name || '';
  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationIdState] = useState(() => {
    // Alleen een numeriek id accepteren — een oude "undefined"/"null"-string zou een
    // URL als /conversations/undefined/messages opleveren → 404 (int-route matcht niet).
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && /^\d+$/.test(stored)) return stored;
    if (stored) localStorage.removeItem(STORAGE_KEY);
    return null;
  });
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [toolStatus, setToolStatus] = useState(null);

  const abortRef = useRef(null);
  // Id van een gesprek dat sendMessage zelf net heeft aangemaakt. Het laad-effect hieronder
  // mag dat gesprek NIET ophalen: de GET komt terug vóórdat de engine het eerste bericht heeft
  // opgeslagen en overschreef dan de optimistische vraag + het antwoord met een lege lijst —
  // "prompt verdwijnt, ik zie niks" bij de eerste vraag (wiki-feedback 26 sep 2026).
  const zelfAangemaaktRef = useRef(null);
  // Generatie van het huidige gesprek: 'Nieuw gesprek' hoogt hem op, zodat een nog lopende
  // aanmaak- of berichtrequest het oude gesprek niet alsnog actief maakt.
  const generatieRef = useRef(0);

  // Persist conversationId to localStorage
  const bewaarConversationId = useCallback((id) => {
    // Alleen een geldig (numeriek) id bewaren; anders wissen.
    const valid = id != null && /^\d+$/.test(String(id));
    setConversationIdState(valid ? String(id) : null);
    if (valid) {
      localStorage.setItem(STORAGE_KEY, String(id));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Publieke setter: null = 'Nieuw gesprek' → lopende requests ongeldig maken en scherm leeg.
  const setConversationId = useCallback((id) => {
    if (id == null) {
      generatieRef.current += 1;
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = null;
      zelfAangemaaktRef.current = null;
      setMessages([]);
      setPendingAction(null);
      setToolStatus(null);
      setIsStreaming(false);
    }
    bewaarConversationId(id);
  }, [bewaarConversationId]);

  // Load existing messages when conversationId changes
  useEffect(() => {
    if (conversationId && conversationId === zelfAangemaaktRef.current) {
      // Net door sendMessage aangemaakt: de berichten staan al (optimistisch) in beeld.
      return;
    }
    if (!conversationId || !userEmail) {
      setMessages([]);
      setPendingAction(null);
      return;
    }

    let cancelled = false;

    const loadConversation = async () => {
      try {
        const res = await authFetch(
          `${AI_ENGINE_URL}/api/v1/chat/conversations/${conversationId}?user_email=${encodeURIComponent(userEmail)}`
        );
        if (!res.ok) {
          // Conversation not found or unauthorized — reset
          if (res.status === 404 || res.status === 403) {
            bewaarConversationId(null);
          }
          return;
        }
        const data = await res.json();
        if (!cancelled) {
          setMessages(data.messages || []);
        }
      } catch (err) {
        console.error('[Assistant] Failed to load conversation:', err);
      }
    };

    const loadPendingActions = async () => {
      try {
        const res = await authFetch(
          `${AI_ENGINE_URL}/api/v1/chat/assistant/conversations/${conversationId}/pending-actions`
        );
        if (res.ok) {
          const data = await res.json();
          if (!cancelled && data?.length > 0) {
            setPendingAction(data[0]);
          }
        }
      } catch (err) {
        console.error('[Assistant] Failed to load pending actions:', err);
      }
    };

    loadConversation();
    loadPendingActions();

    return () => { cancelled = true; };
  }, [conversationId, userEmail, bewaarConversationId]);

  // Build page context from current location
  const getPageContext = useCallback(() => {
    const toolName = resolveToolName(location.pathname);
    // Geopend project/account meesturen: de engine bouwt daar het contextpakket van (meetings, offerte,
    // taken, notities, klantprofiel, huisstijl) — wiki-feedback Joris, 21 sep 2026.
    const project = location.pathname.match(/^\/projects\/(rec[A-Za-z0-9]{14})/);
    const account = location.pathname.match(/^\/accounts\/([A-Za-z0-9_-]{6,})/);
    return {
      path: location.pathname,
      pathname: location.pathname,
      tool_name: toolName || 'Onbekende pagina',
      search: location.search,
      ...(project ? { project_id: project[1] } : {}),
      ...(account ? { account_id: account[1] } : {}),
    };
  }, [location.pathname, location.search]);

  // Pagina van het vorige bericht in dit gesprek. Het gesprek loopt door over pagina's heen; bij een
  // wissel (project 9740 → account Enexis) krijgt de engine de vorige pagina mee, zodat het model het oude
  // project niet vasthoudt (testronde 14 okt 2026). De engine zet de huidige pagina als leidend blok.
  const laatstePaginaRef = useRef({ gesprek: null, pagina: null });
  const metVorigePagina = useCallback((cid, basis) => {
    const huidig = {
      pathname: basis.pathname || basis.path || '',
      ...(basis.project_id ? { project_id: basis.project_id } : {}),
      ...(basis.account_id ? { account_id: basis.account_id } : {}),
    };
    const vorige = laatstePaginaRef.current;
    laatstePaginaRef.current = { gesprek: String(cid), pagina: huidig };
    if (vorige.pagina && vorige.gesprek === String(cid) && vorige.pagina.pathname !== huidig.pathname) {
      return { ...basis, vorige_pagina: vorige.pagina };
    }
    return basis;
  }, []);

  // Send a message
  const sendMessage = useCallback(async (content, pageContext = null) => {
    if (!content.trim() || isStreaming) return;

    const context = pageContext || getPageContext();
    // Alleen een numeriek id gebruiken; een stale "undefined"/"null" → nieuw gesprek.
    let convId = /^\d+$/.test(String(conversationId || '')) ? conversationId : null;

    const generatie = generatieRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    const verlaten = () => controller.signal.aborted || generatie !== generatieRef.current;

    // Maak (indien nodig) een nieuw assistant-gesprek aan. Returnt het id of null.
    const createConversation = async () => {
      try {
        const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/assistant/conversations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_email: userEmail, user_name: userName }),
          signal: controller.signal,
        });
        if (!res.ok || verlaten()) return null;
        const conv = await res.json();
        // Intussen 'Nieuw gesprek' gekozen? Dan dit gesprek niet meer actief maken.
        if (conv?.id == null || verlaten()) return null;
        zelfAangemaaktRef.current = String(conv.id);
        bewaarConversationId(conv.id);
        return conv.id;
      } catch { return null; }
    };

    // Toon de vraag direct — óók als het gesprek nog aangemaakt moet worden.
    const stempel = Date.now();
    const userMsg = {
      role: 'user',
      content,
      id: `temp-user-${stempel}`,
      created_at: new Date().toISOString(),
    };
    const assistantId = `temp-assistant-${stempel}`;
    const assistantMsg = {
      role: 'assistant',
      content: '',
      id: assistantId,
      isStreaming: true,
      created_at: new Date().toISOString(),
    };
    // Werk altijd het eigen antwoord-bericht bij (op id, niet "het laatste bericht"): is de lijst
    // intussen vervangen, dan zetten we vraag + antwoord terug i.p.v. in het niets te schrijven.
    const werkAntwoordBij = (patch) => !verlaten() && setMessages((prev) => {
      if (!prev.some((m) => m.id === assistantId)) {
        const zonderVraag = prev.filter((m) => m.id !== userMsg.id);
        return [...zonderVraag, userMsg, { ...assistantMsg, ...patch }];
      }
      return prev.map((m) => (m.id === assistantId ? { ...m, ...patch } : m));
    });
    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setIsStreaming(true);
    setToolStatus(null);
    setPendingAction(null);

    // Alles binnen try/finally: ook een abort tijdens het aanmaken ('Stop genereren') moet door de
    // opruimcode, anders blijft isStreaming true en is de invoer geblokkeerd.
    try {
      if (!convId) {
        convId = await createConversation();
        if (verlaten()) {
          // Gestopt (zelfde gesprek): lege antwoordballon weg. Na 'Nieuw gesprek' heeft de reset al opgeruimd.
          if (generatie === generatieRef.current) setMessages((prev) => prev.filter((m) => m.id !== assistantId));
          return;
        }
        if (!convId) {
          toast.error('Gesprek aanmaken mislukt');
          setMessages((prev) => prev.filter((m) => m.id !== assistantId));
          return;
        }
      }

      const postMessage = (cid) => authFetch(
        `${AI_ENGINE_URL}/api/v1/chat/assistant/conversations/${cid}/messages`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content, page_context: metVorigePagina(convId, context) }),
          signal: controller.signal,
        }
      );

      let res = await postMessage(convId);
      // Self-heal: gesprek bestaat niet (meer) → maak een nieuw gesprek en probeer 1× opnieuw.
      if (!res.ok && (res.status === 404 || res.status === 403)) {
        const fresh = await createConversation();
        if (fresh) { convId = fresh; res = await postMessage(fresh); }
      }

      if (!res.ok) throw new Error(`API error: ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // SSE-regels kunnen over twee chunks heen vallen: bewaar de onafgemaakte staart.
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (verlaten()) break;
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));

            if (data.event === 'message_delta' || data.content) {
              fullContent += data.content || '';
              werkAntwoordBij({ content: fullContent });
            }

            if (data.event === 'tool_status') {
              const ts = data.tool_status || data;
              if (ts.status === 'done') {
                setToolStatus(null);
              } else {
                setToolStatus(ts);
              }
            }

            if (data.event === 'confirmation_required') {
              setPendingAction(data.action || data);
            }

            if (data.event === 'ui_handoff') {
              // De engine stuurt `target`; path/url blijven voor oudere events.
              // Alleen een allowlist van interne routes — nooit naar buiten navigeren.
              const target = veiligHandoffPad(data.path || data.url || data.target);
              if (target) {
                if (data.session_data) {
                  sessionStorage.setItem('assistant_handoff', JSON.stringify(data.session_data));
                }
                navigate(target);
              }
            }

            if (data.event === 'done' || data.done) {
              werkAntwoordBij({ isStreaming: false });
              setToolStatus(null);
            }

            if (data.event === 'error' || data.error) {
              const msg = data.message || data.error || 'Er ging iets mis';
              toast.error(msg.substring(0, 150));
            }
          } catch (parseErr) {
            // skip unparseable lines
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error(`Assistent fout: ${err.message}`);
        console.error('[Assistant] Stream error:', err);
      }
      // Clean up empty assistant message on error
      if (generatie === generatieRef.current) setMessages((prev) => prev
        .filter((m) => !(m.id === assistantId && !m.content))
        .map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m)));
    } finally {
      // Na 'Nieuw gesprek' heeft de reset al opgeruimd; niet aan een eventueel nieuw gesprek komen.
      if (generatie === generatieRef.current) {
        // Stream klaar zonder 'done'-event: cursor niet eeuwig laten knipperen.
        setMessages((prev) => prev.map((m) => (m.id === assistantId && m.isStreaming ? { ...m, isStreaming: false } : m)));
        setIsStreaming(false);
      }
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [conversationId, isStreaming, userEmail, userName, getPageContext, metVorigePagina, bewaarConversationId, navigate]);

  // Confirm a pending action
  const confirmAction = useCallback(async (actionId) => {
    try {
      const res = await authFetch(
        `${AI_ENGINE_URL}/api/v1/chat/assistant/pending-actions/${actionId}/confirm`,
        { method: 'POST' }
      );
      if (res.ok) {
        setPendingAction(null);
        // De actie kan bewust geblokkeerd zijn (bv. duplicaat) of mislukt — toon dan de
        // reden i.p.v. een onterecht "bevestigd".
        const data = await res.json().catch(() => ({}));
        const r = (data && data.result) || {};
        if (data.success === false || r.blocked || r.error) {
          toast(r.message || r.error || 'Actie niet uitgevoerd', { icon: '⚠️' });
        } else {
          toast.success('Actie bevestigd');
        }
        // Reload messages to get the updated state
        if (conversationId && userEmail) {
          const convRes = await authFetch(
            `${AI_ENGINE_URL}/api/v1/chat/conversations/${conversationId}?user_email=${encodeURIComponent(userEmail)}`
          );
          if (convRes.ok) {
            const data = await convRes.json();
            setMessages(data.messages || []);
          }
        }
      } else {
        toast.error('Bevestiging mislukt');
      }
    } catch (err) {
      toast.error('Bevestiging mislukt');
      console.error('[Assistant] Confirm action failed:', err);
    }
  }, [conversationId, userEmail]);

  // Cancel a pending action
  const cancelAction = useCallback(async (actionId) => {
    try {
      const res = await authFetch(
        `${AI_ENGINE_URL}/api/v1/chat/assistant/pending-actions/${actionId}/cancel`,
        { method: 'POST' }
      );
      if (res.ok) {
        setPendingAction(null);
        toast('Actie geannuleerd');
      } else {
        toast.error('Annulering mislukt');
      }
    } catch (err) {
      toast.error('Annulering mislukt');
      console.error('[Assistant] Cancel action failed:', err);
    }
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);
  const sluit = useCallback(() => setIsOpen(false), []);
  const zichtbaar = useMemo(() => ({ isOpen, sluit }), [isOpen, sluit]);

  const stopStreaming = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  }, []);

  const value = {
    isOpen,
    conversationId,
    messages,
    isStreaming,
    pendingAction,
    toolStatus,
    toggle,
    sluit,
    sendMessage,
    confirmAction,
    cancelAction,
    stopStreaming,
    setConversationId,
  };

  return (
    <AssistantContext.Provider value={value}>
      <AssistantZichtbaarContext.Provider value={zichtbaar}>
        {children}
      </AssistantZichtbaarContext.Provider>
    </AssistantContext.Provider>
  );
}

export function useAssistant() {
  return useContext(AssistantContext);
}

export default AssistantContext;

/** Alleen `{ isOpen, sluit }` van de assistent (zonder hertekenen bij elk bericht). */
export function useAssistantZichtbaar() {
  return useContext(AssistantZichtbaarContext);
}
